import { useTranslation } from "react-i18next";
import { IconButton } from "@/atoms/icon-button";
import { StatusText } from "@/atoms/status-text";
import { EmptyState } from "@/components/empty-state";
import { formatLongDate } from "@/utils/date";
import { formatTableMoney, fromCents } from "@/utils/money";
import { invoiceStatus } from "@/utils/status";
import type { PortalInvoice } from "@/data/portal";

/**
 * What the client paid (template detail 1.7, body; spec row-52-default 1.2).
 *
 * One 64 row per invoice, folded at 390 and flat from 640. On a phone it reads as two lines: the
 * reference in the mono face beside the amount, then the date beside the state. From 640 the four
 * facts run along one line, the number and the date separated by a middle dot. The document is an
 * icon-only control with a spoken name that says which invoice it belongs to, and it trails both
 * lines either way.
 *
 * An invoice with no number is drawn from the first eight of its id, upper-cased, in the mono
 * face: it is still the thing a person quotes back to their coach.
 *
 * The status word and the download control are the last two columns and they line up down the
 * list, because the document slot is drawn even when there is no document.
 */

export interface InvoicesSectionProps {
  invoices: PortalInvoice[];
}

export function InvoicesSection({ invoices }: InvoicesSectionProps) {
  const { t } = useTranslation();

  if (invoices.length === 0) {
    return <EmptyState heading={t("gymkeePay.portal.noInvoice")} />;
  }

  return (
    <ul className="flex flex-col">
      {invoices.map((invoice) => {
        const number = invoice.number ?? invoice.id.slice(0, 8).toUpperCase();
        const date = formatLongDate(invoice.paidAt ?? invoice.createdOn);
        const status = invoiceStatus(invoice.status);

        return (
          <li
            key={invoice.id}
            className="ds-row ds-row-comfortable ds-hairline gap-gk-3 px-0 sm:gap-gk-4 sm:min-h-[var(--gk-row-default)]"
          >
            {/* The table becomes rows at 390 (DS19's order of sacrifice): the four facts fold
                into two lines rather than four columns squeezed to nothing, which is what put
                "1 September 2026" on three lines in a 70 wide column. Line 1 is the reference
                and the money, line 2 is the date and the state, and the two columns still line
                up down the list. One DOM: the grid places the four spans at 390 and lets them
                run as a single line from 640. */}
            <div className="gap-x-gk-3 gap-y-gk-1 grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-center sm:flex sm:items-baseline sm:gap-gk-2">
              <span className="gk-mono col-start-1 row-start-1 min-w-0 text-[var(--gk-text-primary)] sm:col-auto sm:row-auto">
                {number}
              </span>
              {/* The dot separates the two facts only while they share a line. */}
              {date && (
                <span
                  aria-hidden="true"
                  className="hidden flex-none text-[13px] text-[var(--gk-text-tertiary)] sm:inline"
                >
                  ·
                </span>
              )}
              {date && (
                <span className="col-start-1 row-start-2 min-w-0 text-[13px] text-[var(--gk-text-secondary)] sm:col-auto sm:row-auto">
                  {date}
                </span>
              )}
              {/* The money and the state sit at the right of their own lines at 390 and become
                  the last two columns of the single line from 640, where the amount takes the
                  free width so every amount in the list stops at the same pixel. */}
              <span className="gk-num col-start-2 row-start-1 justify-self-end text-right text-[14px] font-semibold whitespace-nowrap text-[var(--gk-text-primary)] sm:col-auto sm:row-auto sm:ml-auto sm:flex-none">
                {formatTableMoney(fromCents(invoice.total), invoice.currency)}
              </span>
              <span className="col-start-2 row-start-2 flex justify-self-end sm:col-auto sm:row-auto sm:min-w-gk-24 sm:flex-none sm:justify-start">
                {status && (
                  <StatusText
                    size={13}
                    word={t(status.key)}
                    tone={status.tone}
                  />
                )}
              </span>
            </div>
            {/* The document trails both lines, centred against them. Its slot is reserved when
                there is no document, so the columns above it do not shift row to row. */}
            {invoice.invoicePdf ? (
              <IconButton
                icon="download"
                label={t("gymkeePay.portal.downloadInvoiceNamed", { number })}
                href={invoice.invoicePdf}
                target="_blank"
                className="ds-pay-hand flex-none self-center"
              />
            ) : (
              <span
                aria-hidden="true"
                className="ds-pay-hand ds-icon-btn h-gk-10 w-gk-10 flex-none self-center border-0 bg-transparent"
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
